import $ from "jquery";
import _ from "lodash";
import atlas, {AuthenticationType, ControlPosition, Map, MapErrorEvent} from "azure-maps-control";
import {NavUtils} from "../../util/NavUtils";
import {AzureWeatherLayer} from "../layer/AzureWeatherLayer";
import {AzureImageSpriteManager} from "@esWebAPI/es/map/layer/AzureImageSpriteManager";
import {AzureScaleBar} from "@esWebAPI/es/map/layer/basemap/AzureScaleBar";
import {transformRequestForFontGlyph} from "@esWebAPI/es/model/Fonts";
import {AzureOverlay} from "./AzureOverlay";
import {DatasourceManager} from "./DatasourceManager";
import {padBounds, padLonLat} from "../util";

import type {CameraBoundsOptions, CameraOptions, ServiceOptions, StyleOptions, UserInteractionOptions} from "azure-maps-control";
import type {GeoMap, ViewPoint} from "@esWebAPI/es/model/GeoMap";
import type {Feature} from "@esWebAPI/es/model/Feature";
import type Layer from "@esWebAPI/es/map/module/Layer";
import type TerritoryModel from "@esWebAPI/es/map/TerritoryModel";
import type {IMapLayer} from "@esWebAPI/es/map/layer/MapLayer";
import {IEventChannel} from "@esWebAPI/es/model/Models";

const overlayIds = AzureOverlay.getLayerIds(); // overlays are not added through the AzureBaseMap

export class AzureBaseMap {
    private readonly baseMapTypes = ['roadmap', 'satellite', 'satellite_road_labels', 'grayscale_dark', 'night'];
    private readonly customStyles = [
        {id: 41, name: "plain_white", label: "Plain White"},
        {id: 24, name: "light_dream", label: "Light Dream"},
        {id: 2, name: "muted_blue", label: "Muted Blue"},
        {id: 11, name: "just_places", label: "Just Places"},
        {id: 5, name: "blue_gray", label: "Blue Grey"},
        {id: 25, name: "light_gray", label: "Light Grey"},
        {id: 10, name: "applemapsesque", label: "AppleMapsesque"}
    ];

    private element: HTMLElement;

    private readonly _map: Map;
    private readonly datasourceManager: DatasourceManager;
    private readonly _loadComplete: JQuery.Deferred<any> = $.Deferred<any>();
    protected readonly eventChannel: IEventChannel;

    private readonly weatherTileLayer: AzureWeatherLayer;

    private baseMapStyleId: number | undefined = undefined;
    private baseMapTypeId = "roadmap";
    private paddingPx = [5, 5, 5, 5];

    private readonly _imageSpriteManager: AzureImageSpriteManager;
    private readonly _layers: IMapLayer[] = [];

    constructor(layerModule: Layer, territoryModel: TerritoryModel, element: HTMLElement, geoMap: GeoMap, eventChannel: IEventChannel) {

        this.element = element;
        this.eventChannel = eventChannel;
        this.baseMapTypeId = geoMap.baseMapType;

        if (!atlas.isSupported(true)) {
            const msg = $i18n("error_map_browser_supported_caveats");
            es.util.TrackingEvents.push(['UN_UI_ERROR_' + msg]);
            es.log.warn(msg);
        }

        const baseMapType = geoMap.baseMapType;
        const baseMapStyleId = geoMap.baseMapStyleId;

        let key = es.azureMapsKey;
        // A S1 Tier key is required for Satellite & Hybrid
        if (baseMapType && baseMapType.indexOf("satellite") > -1) {
            key = es.azureMapsAdvancedKey;
        }

        atlas.setLanguage("auto");
        const mapOptions = {
            view: 'Auto',
            zoom: NavUtils.resolutionToZoom(geoMap.viewPoint.resolution),
            pitch: parseFloat("" + geoMap.viewPoint?.pitch) || 0,
            bearing: parseFloat("" + geoMap.viewPoint?.bearing) || 0,
            style: this.getAzureMapType(baseMapType),
            preserveDrawingBuffer: true,
            showLogo: false,
            showFeedbackLink: false,
            showTileBoundaries: false,
            authOptions: {
                authType: AuthenticationType.subscriptionKey,
                subscriptionKey: key
            },
            transformRequest: transformRequestForFontGlyph // pre-flight request processor (similar to a servlet filter)
        } as ServiceOptions & StyleOptions & UserInteractionOptions & (CameraOptions | CameraBoundsOptions);

        if (geoMap.viewPoint.x !== 0 && geoMap.viewPoint.y !== 0) {
            mapOptions.center = [geoMap.viewPoint.x, geoMap.viewPoint.y];
        }

        try {
            this._map = new Map(element, mapOptions);
        } catch (e) {
            // try again, just in case it's a temporary issue
            es.log.log("azure map init failure. try again...", e);
            try {
                this._map = new Map(element, mapOptions);
            } catch (ee) {
                const errMsg = $i18n("error_map_browser_not_supported", ["https://help.espatial.com/failed-to-initialize-webgl"]);
                es.alert.alertOk(errMsg);
                this._loadComplete.reject();
                es.log.error(errMsg, e, ee);
                return;
            }
        }

        this.datasourceManager = new DatasourceManager(this._map, layerModule, territoryModel);
        this._imageSpriteManager = new AzureImageSpriteManager(this._map, this.eventChannel);

        this.weatherTileLayer = new AzureWeatherLayer(this._map, geoMap.weatherOverlay, this.eventChannel);
        this._map.events.add("ready", () => {

            this._map.controls.add(
                new AzureScaleBar({units: 'imperial'}), {position: ControlPosition.BottomLeft}
            );

            // Apply the custom style after the map is ready
            if (baseMapStyleId) {
                return this.setStyleById(baseMapStyleId, true).then(() => {
                    if (es.util.EventChannel_logging) {
                        es.log.log("azureBaseMap _loadComplete");
                    }
                    this._loadComplete.resolve();
                });
            }

            if (es.util.EventChannel_logging) {
                es.log.log("azureBaseMap _loadComplete");
            }
            this._loadComplete.resolve();
        });

        // Detect an Azure Maps service outage (5xx from atlas.microsoft.com) during initial load so a stuck
        // spinner becomes the dismissable "taking longer than expected" overlay message rather than an endless spinner.
        let azureErrorHandled = false;
        const AZURE_ERROR_GRACE_MS = 10000;
        this._map.events.add("error", (e: MapErrorEvent) => {
            if (azureErrorHandled) {
                return;
            }

            const err = e.error as (Error & { status?: number; url?: string });
            const url = err?.url || err?.message || "";
            const isAtlas5xx = typeof err?.status === "number" && err.status >= 500 && err.status < 600 &&
                url.indexOf("atlas.microsoft.com") > -1;
            if (!isAtlas5xx || this._loadComplete.state() !== "pending") {
                return;
            }
            azureErrorHandled = true;

            const confirmStuck = () => {
                if (this._loadComplete.state() !== "pending") {
                    return; // map recovered - nothing to report
                }
                if (document.hidden) {
                    // tab is backgrounded: defer the decision until the user is actually looking at the map
                    document.addEventListener("visibilitychange", confirmStuck, {once: true});
                    return;
                }
                es.log.error("Azure Maps service error during map init (" + err.status + "): " + url);
                es.api.logging.clientsideErr("Azure Maps service error (" + err.status + ") during map init: " + url);
                $().loadingOverlay("longLoadingMessage");
            };

            window.setTimeout(confirmStuck, AZURE_ERROR_GRACE_MS);
        });

        this._map.events.add("moveend", () => {
            //console.log("moveend", e);

            //this.fireOnEsMapFullyRendered();

            // TODO: this event was firing with mercator so keeping it that way for compatibility with existing code but we should change it. see: TOR9-19134
            const center = this.getCenter();

            // TODO: think we refactor out code that relies on centerChanged, resolutionChanged once dead code is cut. actually, may need resolutionChanged for labelling iin territories will talk to AD
            const centerChanged = true;
            const resolutionChanged = true;

            this.eventChannel.fireEvent("onUpdateView", center[0], center[1], this.getResolution(), centerChanged, resolutionChanged);
        });

        this.createTooltip(this._map);
        this.createChartContainer(this._map, false);
    }
/*
    public fireOnEsMapFullyRendered(): void {
        // todo: not entirely sure onEsMapFullyRendered will always be correct here. may need special cases and to throw it somewhere else or after some other event
        const timeout = setTimeout(() => {
            es.log.warn("onEsMapFullyRendered called after 1000ms but idle not triggered. going to fire onEsMapFullyRendered anyway"); // it might be idle already
            this.eventChannel.fireEvent("onEsMapFullyRendered");
        }, 2000);

        this.map.events.addOnce("idle", () => {
            clearTimeout(timeout);
            this.eventChannel.fireEvent("onEsMapFullyRendered");
        });
    }
*/
    private createTooltip(map: Map): void {
        const tooltip = document.createElement('div');
        tooltip.id = "_es_tooltip";

        Object.assign(tooltip.style, {
            position: 'absolute',
            padding: '4px',
            background: 'white',
            border: '1px solid #333333',
            borderRadius: '4px',
            display: 'none',
            textAlign: 'left',
            pointerEvents: 'none',
        } as CSSStyleDeclaration);

        map.getCanvasContainer().appendChild(tooltip);
    }

    private createChartContainer(map: Map, isPie = true): void {
        const chartContainer = document.createElement('div');
        chartContainer.id = "_es_chart_container";
        const width = 75;
        const height = 75;

        Object.assign(chartContainer.style, {
            position: 'absolute',
            zIndex: '1000',
            background: 'transparent',
            padding: '0',
            borderRadius: (isPie ? 50 : 1 ) + '%',
            //borderRadius: '50%',
            width: width + 'px',
            height: height + 'px',
            overflow: 'hidden',
            display: 'none',
            pointerEvents: 'none',
        } as CSSStyleDeclaration);
        

        const pieCanvas = document.createElement('canvas');
        pieCanvas.id = "_es_chart_pieChart";
        pieCanvas.width = width;
        pieCanvas.height = height;
        
        chartContainer.appendChild(pieCanvas);

        map.getCanvasContainer().appendChild(chartContainer);
    }

    private toggleKey() {
        if (this._map) {
            let key = es.azureMapsKey;
            if (this.baseMapTypeId && this.baseMapTypeId.indexOf("satellite") > -1) {
                key = es.azureMapsAdvancedKey;
            }

            const authOptions = this._map.getServiceOptions().authOptions;
            if (authOptions && key !== authOptions.subscriptionKey) {
                authOptions.subscriptionKey = key;
                this._map.setServiceOptions({authOptions: authOptions});
            }
        }
    }

    public setCenter(lon: number, lat: number, applyPadding?: boolean, disableAnimation?: boolean) {
        if (applyPadding) {
            [lon, lat] = padLonLat(lon, lat, this.getResolution(), this.paddingPx);
        }

        //es.log.log("setCenter setCamera", lon, lat, applyPadding, disableAnimation);
        if (es.debugShowOverlayOnSetBounds) {
            esApp.map.overlay.pulsePoint([lon, lat], true, 5000);
        }

        this._map.setCamera({
            center: [lon, lat],
            type: "ease",
            //padding: applyPadding ? this.getPaddingPx() : {top: 0, bottom: 0, left: 0, right: 0},
            pitch: this.getPitch(),
            bearing: this.getBearing(),
            duration: disableAnimation ? 0 : 200
        });
    }

    public getDatasourceManager() {
        return this.datasourceManager;
    }

    public setZoomBy(zoom: number) {
        es.log.log("setZoomBy setCamera", zoom);
        this._map.setCamera({
            zoom: this.getZoom() + zoom,
            type: "ease",
            duration: 200
        });
    }

    /**
     * set exact zoom with no animation. useful for setting view quickly as with exports
     * @param zoom
     */
    public setZoom(zoom: number) {
        es.log.log("setZoom setCamera", zoom);
        this._map.setCamera({zoom: zoom});
    }

    public setResolution(resolution: number) {
        es.log.log("setResolution setCamera", resolution);
        this._map.setCamera({zoom: NavUtils.resolutionToZoom(resolution)});
    }

    /**
     *
     * @param x lon
     * @param y lat
     * @param zoom
     */
    public setCenterAndZoom(x: number, y: number, zoom?: number) {
        //es.log.log("setCenterAndZoom setCamera", x, y, zoom);
        const camOpts = {
            center: [x, y]
        } as CameraOptions;
        if (zoom) {
            camOpts.zoom = zoom
        }
        this._map.setCamera(camOpts);
    }

    /**
     *
     * @param lon
     * @param lat
     * @param resolution note: if resolution is undefined (as it often is for layers without a maxRes, for example) then no zoom will occur. 1.2 is used to zoom to a point in several places todo: create a constant for this
     * @param disableAnimation
     */
    public async setCenterAndResolution(lon: number, lat: number, resolution?: number, disableAnimation?: boolean): Promise<void> {
        const camOpts = {
            center: [lon, lat],
            // make zoom time dependent on scale as when zoomed out the jump is too much. sanity limit of 5 sec but shouldn't ever hit this
            duration: disableAnimation ? 0 : Math.min(((20 - (this._map.getCamera().zoom || 1)) / 5) * 1000, 5000)
        } as CameraOptions;

        if (resolution) {
            camOpts.zoom = NavUtils.resolutionToZoom(resolution);
        }

        es.log.log("setCenterAndResolution", camOpts);
        //this._map.setCamera(camOpts);
        this.getMapLibreMap().flyTo(camOpts);

        return new Promise<void>(resolve => {
            setTimeout(resolve, camOpts.duration);
        });
    }

    /**
     * esApp.map.baseMap._map.setCamera({bounds: [-124.08367, 25.62858, -68.83597, 48.20082], padding: {top: 75, bottom: 250, left: 75, right: 75}})
     * @param bounds latlon e.g. [-124.08367, 25.62858, -68.83597, 48.20082]
     * @param padding defaults to getPaddingPx() if not provided
     * @param disableAnimation default false
     */
    public async setBounds(
        bounds: number[],
        padding?: any | undefined,
        disableAnimation = false
    ): Promise<void> {

        if(es.util.bounds.isBoundsSinglePoint(bounds)){
            return this.setCenterAndResolution(bounds[0], bounds[1], 1.2, disableAnimation);
        }

        const duration = disableAnimation ? 0 : 750;

        const camOpts = {
            bounds: bounds,
            padding: padding || this.getPaddingPx(),
            pitch: this.getPitch(),
            bearing: this.getBearing(),
            duration: duration,
            type: 'ease'
        } as CameraOptions;


        es.log.log("setBounds", camOpts);

        this._map.setCamera(camOpts);

        return new Promise<void>(resolve => {
            setTimeout(resolve, duration);
        });
    }

    public setCursor(cursor: string) {
        this._map.getCanvasContainer().style.cursor = cursor;
    }

    /**
     * A GeoJSON BoundingBox object - an array that defines a shape whose edges follow lines of constant longitude, latitude
     * @return [west, south, east, north]
     */
    public getBounds() {
        const bounds = this._map.getCamera().bounds;
        if (!bounds) {
            throw new Error("Camera has no bounds");
        }
        return bounds;
    }

    public getResolution() {
        return NavUtils.zoomToResolution(this._map.getCamera().zoom || 1);
    }

    /**
     * Get map center lonLat
     */
    public getCenter(): [number, number] {
        return this._map.getCamera().center as [number, number] || [0, 0];
    }

    public getPitch(): number | undefined {
        let rs = this._map.getCamera().pitch !== 0 ? this._map.getCamera().pitch : 0;
        rs = parseFloat(es.util.round(rs, 7));
        return rs;
    }

    public getBearing(): number | undefined {
        let rs = this._map.getCamera().bearing !== 0 ? this._map.getCamera().bearing : 0;
        rs = parseFloat(es.util.round(rs, 7));
        return rs;
    }

    public resetPitchAndBearing() {
        const camOpts = {
            pitch: 0,
            bearing: 0,
            duration: 250,
            type: 'ease'
        } as CameraOptions;

        es.log.log("resetPitchAndBearing", camOpts);
        this._map.setCamera(camOpts);
    }

    public getZoom() {
        const zoom = this._map.getCamera().zoom;
        if (!zoom) {
            throw new Error("Camera has no zoom");
        }
        return zoom;
    }

    /**
     * Convert our internal map types to Azure Maps types
     * @param mapType eSpatial map type
     * @private
     */
    private getAzureMapType(mapType: string) {
        if (mapType === "roadmap") {
            return "road";
        }
        if (this.baseMapTypes.indexOf(mapType) > -1) {
            return mapType;
        }
        es.log.warn("Style not supported: " + mapType);
        return "road";
    }

    private setMapStyle(style: any): Promise<void> {
        console.time("setMapStyle");
        return new Promise((resolve, reject) => {
            const SET_STYLE_TIMEOUT = 5000;
            const SET_STYLE_CHECK_LOAD_TIMEOUT = 200;
            let timeoutId: number|undefined = undefined;

            try {
                // Insert the Azure Key in the style
                let url = style.sources.vectorTiles.tiles[0];
                url = url.replaceAll('{azure-key}', es.azureMapsKey);
                style.sources.vectorTiles.tiles[0] = url;
                es.log.log("setMapStyle : loading style", style);

                // Fail-safe timeout to reject the promise if the style doesn't load in time
                timeoutId = window.setTimeout(() => {
                    if (!this.getMapLibreMap().isStyleLoaded()) {
                        es.log.error("setMapStyle : style load timeout " + SET_STYLE_TIMEOUT);
                        this.getMapLibreMap().off('styledata', isStyleLoaded);
                        reject(new Error("Style failed to load within timeout " + SET_STYLE_TIMEOUT));
                    }
                }, SET_STYLE_TIMEOUT);

                // Verify style loading with isStyleLoaded
                const isStyleLoaded = () => {
                    if (this.getMapLibreMap().isStyleLoaded()) {
                        //es.log.log("setMapStyle : style verified loaded");
                        window.clearTimeout(timeoutId);
                        this.getMapLibreMap().off('styledata', isStyleLoaded);
                        console.timeEnd("setMapStyle");
                        resolve();
                    } else {
                        //es.log.log("setMapStyle : style not yet loaded, waiting...");
                        window.setTimeout(isStyleLoaded, SET_STYLE_CHECK_LOAD_TIMEOUT);
                    }
                };

                // Set up event listener for when the style starts loading
                this.getMapLibreMap().once('styledata', () => {
                    isStyleLoaded();
                });
                this.getMapLibreMap().setStyle(style); // , {diff: true} - not sure we can use this, see:

            } catch (error) {
                window.clearTimeout(timeoutId); // Clear timeout on error
                es.log.error("setMapStyle : ERROR", error);
                console.timeEnd("setMapStyle");
                reject(error); // Reject the promise if there's an error
            }
        });
    }


    private waitForLoad() {
        if (this.getMapLibreMap().getStyle()?.layers) {
            return es.util.deferred.resolved();
        }

        const dfd = $.Deferred();
        this.getMapLibreMap().once('load', () => dfd.resolve());

        return dfd.promise();
    }

    public setStyleById(styleId: number, ignoreFireEvent?: boolean) {
        // TODO
        // if(isMobile) {
        //     //CustomStyles are not compatible with mobile rendering
        //     return es.util.deferred.resolved();
        // }
        this.baseMapStyleId = styleId;
        const styleName = this.getStyleNameById(styleId);

        let dfd;
        if (!styleName) {
            dfd = es.util.deferred.resolved("roadmap", {});
        } else {
            dfd = this.waitForLoad().then(() => {
                const layersIds = this._layers.flatMap(l => l.getLayerIds()).concat(overlayIds);
                const mapStyle = this.getMapLibreMap().getStyle();
                const toMergeLayers = mapStyle.layers.filter((l: any) => layersIds.includes(l.id));
                const toMergeSources = toMergeLayers.map((l: any) => l.source).reduce((a: any, s: any) => {
                    a[s] = mapStyle.sources[s];
                    return a;
                }, {});

                let appUrl = es.appUrl;
                if (es.isPublicAccess) {
                    appUrl = es.publicUrl;
                }

                return es.api.getRequestBroker().doGet({
                    url: appUrl + 'basemap-styles/' + styleName + '.json?version=' + es.ver
                }).then((style: any) => {
                    style.layers.push(...toMergeLayers);
                    style.sources = {...style.sources, ...toMergeSources};
                    return this.setMapStyle(style).then(() => {
                        this.weatherTileLayer.reApply();
                        return es.util.deferred.resolved("roadmap", style);
                    }).catch(err => {
                        // what to do? let the basemap load fail and try to continue on or treat it as a catastrophic failure?
                        console.error("setStyleById : error but continue loading", err);
                        return es.util.deferred.resolved("roadmap", style);
                    });
                });
            });
        }

        return dfd.done(() => {
            if (!ignoreFireEvent) {
                this.eventChannel.fireEvent("onSetBaseMap", {baseMapType: "roadmap", baseMapStyleId: styleId});
            }
        });
    }

    private getStyleNameById(styleId: number) {
        for (const style of this.customStyles) {
            if (styleId === style.id) {
                return style.name;
            }
        }
        es.log.log('StyleId not supported: ' + styleId);
        return null;
    }

    public getStyleId() {
        if (this.baseMapStyleId && this.isStyleAvailable(this.baseMapStyleId)) {
            return this.baseMapStyleId;
        }
        return null;
    }

    public isStyleAvailable(styleId: number) {
        return this.customStyles.findIndex(s => s.id == styleId) >= 0;
    }

    public getBaseMapTypes() {
        return this.baseMapTypes.slice();
    }

    public getWeatherLayer() {
        return this.weatherTileLayer;
    }

    public getBaseMapCustomStylesObjRef() {
        //return this.customStyles.slice();
        return this.customStyles;
    }

    public toggleTrafficLayer() {
        if (this.isTrafficActive()) {
            this.map.setTraffic({flow: "none"});
        } else {
            this.map.setTraffic({flow: "relative"});
        }
    }

    public isTrafficActive() {
        return this._map.getTraffic().flow !== "none";
    }

    public getType() {
        return this.baseMapTypeId;
    }

    public setType(newBaseMapTypeId: string) {

        if (newBaseMapTypeId === this.baseMapTypeId && this.baseMapStyleId == undefined) {
            return;
        }

        this.baseMapStyleId = undefined;
        this.baseMapTypeId = newBaseMapTypeId;
        this.toggleKey();
        this._map.setStyle({style: this.getAzureMapType(newBaseMapTypeId)});
        this.eventChannel.fireEvent("onSetBaseMap", {baseMapType: newBaseMapTypeId, baseMapStyleId: null});
    }

    public getMapLibreMap() {
        // @ts-ignore
        return this.map.map;
    }

    // TODO: rename to getAzureMap or getAtlasMap ?
    get map() {
        return this._map;
    }

    get loadComplete(): JQuery.Deferred<any> {
        return this._loadComplete;
    }


    get imageSpriteManager(): AzureImageSpriteManager {
        return this._imageSpriteManager;
    }

    /* ------   Layers related Methods     -------- */

    get layers(): IMapLayer[] {
        return this._layers;
    }

    /**
     *
     * @param layer
     */
    public addLayer(layer: IMapLayer) {
        this._layers.push(layer);
    }

    /**
     * Get a rendering layer by its internal id
     * @param {string} layerId  MapLibre Internal id
     */
    public getLayerById(layerId: string) {
        const res = this._layers.filter(layer => layer.getLayerIds().includes(layerId));
        if (res.length > 1) {
            throw new Error("Multiple layers found for the same layer id: " + layerId);
        }
        if (res.length == 1) {
            return res[0];
        } else {
            return undefined;
        }
    }

    /**
     * Get a list of rendering layers by its LayerViewId
     * If no layer exists an empty array is returned;
     * @param {number} layerViewId LayerViewId
     * todo: rename to getIMapLayersByLayerViewId?
     * todo: return MapLayer?
     */
    public getLayersByLayerViewId(layerViewId: number): IMapLayer[] {
        return this._layers.filter(layer => layer.layerView.id === layerViewId);
    }

    public removeLayer(layerViewId: number) {
        // there can be more than one layer with the same layerViewId (e.g. heatmap + overlay pin)
        const layersToRemove = this._layers.filter(lv => lv.layerView.id === layerViewId);

        if (layersToRemove.length === 0) {
            es.log.warn("no layers found with lvId", layerViewId, this._layers);
            return;
        } else if(layersToRemove.length > 1) {
            es.log.warn("multiple layers found with id", layerViewId, this._layers); // should be heatmap only
        }

        layersToRemove.forEach(layer => {
            layer.destroy();
            this._layers.splice(this._layers.indexOf(layer), 1);
        });
    }

    public removeAllLayer() {
        this._layers.forEach((layer: IMapLayer) => {
            layer.destroy();
        });
        this._layers.splice(0, this._layers.length);
    }

    /* ------  End Layers related Methods     -------- */

    /**
     * Get viewPoint object (com.espatial.es.model.geom.ViewPoint) as used by geoMap.viewPoint
     */
    public getCurrentViewPoint(): ViewPoint {
        const center = this.getCenter();
        return {
            resolution: this.getResolution(),
            x: center[0],
            y: center[1],
            pitch: this.getPitch(),
            bearing: this.getBearing()
        };
    }

    /**
     * Convenience method to zoom to a single or multiple lv extents
     * e.g.:
     *     esApp.map.baseMap.zoomToLayerViewExtents([lv.getId()]);
     *     esApp.map.baseMap.zoomToLayerViewExtents(esApp.map.layer.getLayerViewIds()); // zoomToMapExtents
     * @param lvIds
     * @param isPadBoundsRequired
     * @param disableAnimation
     */
    public async zoomToLayerViewExtents(lvIds: number[], isPadBoundsRequired = false, disableAnimation = false): Promise<number[]> {

        if (lvIds.length === 0) {
            es.log.warn("zoomToLayerViewExtents called with empty lvIds array.")
            return Promise.resolve([]);
        }

        const promises: Promise<any>[] = [];
        lvIds.forEach((layerViewId) => {
            promises.push(es.api.feature.bounds(layerViewId, {"$type": "All"}));
        });

        const layersBounds = await Promise.all(promises);

        let bounds: number[] = [];
        layersBounds
            .filter(layerBounds => layerBounds && !es.util.bounds.equal(layerBounds, [0, 0, 0, 0]))
            .forEach((layerBounds, i) => {
                if (i === 0) {
                    bounds = layerBounds;
                } else {
                    es.util.bounds.union(layerBounds, bounds, bounds);
                }
            });

        if (!bounds?.length || es.util.bounds.isEmpty(bounds) || es.util.bounds.isAllZero(bounds)) {
            es.log.warn("Skip set bounds for", bounds);
            return [];
        }

        if(es.util.bounds.isBoundsSinglePoint(bounds)){
            await this.setCenterAndResolution(bounds[0], bounds[1], 1.2);
        } else {
            await this.setBounds(bounds, isPadBoundsRequired ? this.getPaddingPx() : null, disableAnimation);
        }

        return bounds;

    }

    public locateToFeatureById(layerViewId: number, featureId: number, noZoom?: false) {
        const _this = this;
        es.api.feature.get(layerViewId, featureId).done((feature: any) => {
            return _this.locateToFeature(feature, -1, noZoom);
        }).fail(function (e: any) {
            throw new Error("Error loading Feature: " + e);
        });
    }

    public async locateToFeature(feat: Feature, maxResolution?: number, noZoom = false, disableAnimation = false): void {
        let lon: number | undefined;
        let lat: number | undefined;
        let useBounds = false;
        const bounds: [number, number, number, number] = es.api.geom.getBounds(feat.geom);
        if (["PolygonStyle", "LineStyle"].includes(feat.style.$type)) {
            // get centre of bounds;
            lon = (bounds[0] + bounds[2]) / 2;
            lat = (bounds[1] + bounds[3]) / 2;
            useBounds = true;
        } else if (feat.geom[0] === "MG") {
            const pointGeom = _.find(feat.geom, (g:any) => g[0] === "PT");
            if (pointGeom) {
                lon = pointGeom[1];
                lat = pointGeom[2];
            }
        } else if (feat.geom[0] === "PT") {
            lon = feat.geom[1];
            lat = feat.geom[2];
        }

        if (!lon || !lat) {
            es.log.warn("locateToFeature: Invalid coordinates for feature", feat);
            return;
        }

        if (noZoom) {
            this.setCenter(lon, lat);
            return;
        }

        if (useBounds) {
            this.setBounds(bounds, this.getPaddingPx(), disableAnimation);
            return;
        }

        // if user keep clicking zoom then keep zooming in up, until 0.1 res
        let resToZoomTo = Math.min(this.getResolution() < 1.2 ? this.getResolution() / 2 : 1.2);
        resToZoomTo = Math.max(resToZoomTo, 0.1);

        // use maxResolution only if it's a valid positive number, otherwise use calculated resolution
        const finalResolution = (maxResolution && isFinite(maxResolution) && maxResolution > 0)
            ? maxResolution
            : resToZoomTo;

        await this.setCenterAndResolution(lon, lat, finalResolution, disableAnimation);
    }

    public getSizePx() {
        return [this.element.clientWidth, this.element.clientHeight];
    }

    public setSizePx(xPx: number, yPx: number) {
        this.element.style.width = xPx + "px";
        this.element.style.height = yPx + "px";
        this._map.resize();
        this.eventChannel.fireEvent("onUpdateSizePx", xPx, yPx, this.getCenter(), this.getResolution());
    }

    /**
     * Ensure that the bounds given are visible, doing the least amount of zooming / panning possible if the target bounds
     * intersect with the current view, otherwise recenter on the target bounds.
     * @param targetBounds
     * @param disableAnimation
     * @param isZoomAllowed
     */
    public async ensureBoundsVisible(targetBounds: number[], disableAnimation = false, isZoomAllowed = true) {

        const viewBounds = padBounds(this.getBounds(), this.getResolution(), this.paddingPx.slice());

        if (es.debugShowOverlayOnSetBounds) {
            // 80% transparent fill so the debug box is not too dark/severe (default is 50%)
            const style = AzureOverlay.getDefaultPolygonStyle(es.util.color.BLACK, 80);
            esApp.map.overlay.drawBounds(targetBounds, style, 3000);
            esApp.map.overlay.drawBounds(viewBounds, style, 5000);

            es.log.log("ensureBoundsVisible",
                "intersect", es.util.bounds.intersect(viewBounds, targetBounds),
                "contains", es.util.bounds.contains(viewBounds, targetBounds),
                targetBounds
            );
        }

        // if bounds intersect with current view pan as little as possible, otherwise recenter.
        if (es.util.bounds.contains(viewBounds, targetBounds)) {
            //es.log.log("view contains target bounds - skip");
        } else if (es.util.bounds.intersect(viewBounds, targetBounds)) {
            const scaleX = (targetBounds[2] - targetBounds[0]) / (viewBounds[2] - viewBounds[0]);
            const scaleY = (targetBounds[3] - targetBounds[1]) / (viewBounds[3] - viewBounds[1]);
            const scale = Math.max(scaleX, scaleY);
            const center = this.getCenter();
            const oldResolution = this.getResolution();
            const newResolution = oldResolution;
            if (scale > 1 && isZoomAllowed) { // either the width or the height is greater than the current, so we need to zoom out, otherwise we dont
                this.getMapLibreMap().fitBounds(targetBounds, {padding: this.paddingPx[0]})
                return;
            }

            let cx = center[0];
            if ((targetBounds[2] - targetBounds[0]) > (viewBounds[2] - viewBounds[0])) {
                cx = (targetBounds[0] + targetBounds[2]) / 2;
            } else if (targetBounds[0] < viewBounds[0]) {
                cx += targetBounds[0] - viewBounds[0];
            } else if (targetBounds[2] > viewBounds[2]) {
                cx += targetBounds[2] - viewBounds[2];
            }

            let cy = center[1];
            if ((targetBounds[3] - targetBounds[1]) > (viewBounds[3] - viewBounds[1])) {
                cy = (targetBounds[1] + targetBounds[3]) / 2;
            } else if (targetBounds[1] < viewBounds[1]) {
                cy += targetBounds[1] - viewBounds[1];
            } else if (targetBounds[3] > viewBounds[3]) {
                cy += targetBounds[3] - viewBounds[3];
            }

            const newCenter = [cx, cy];
            if (newResolution !== oldResolution) {
                await this.setCenterAndResolution(newCenter[0], newCenter[1], (isZoomAllowed ? newResolution : undefined), disableAnimation);
            } else {
                this.setCenter(newCenter[0], newCenter[1], false, disableAnimation);
            }

        } else {
            await this.setBounds(targetBounds, this.getPaddingPx(), disableAnimation);
        }
    }

    /**
     * Ensure pixel bounds are visible by panning to fit the popup on screen. Usually used for popups,
     * e.g.:
     *      esApp.map.basemap.ensurePopupBoundsVisible(map.popup.getPixelBounds(popupId));
     * @param pixelBounds
     */
    public ensurePixelBoundsVisible(pixelBounds: number[]): void {
        // validate pixelBounds to prevent NaN errors
        if (!pixelBounds || pixelBounds.length < 4) {
            es.log.warn("ensurePixelBoundsVisible: Invalid pixelBounds (too short or missing)", pixelBounds);
            return; // skip - it's going to fail anyway
        }

        // check for NaN or invalid values
        if (pixelBounds.some(val => !isFinite(val))) {
            es.log.warn("ensurePixelBoundsVisible: Invalid pixelBounds contain NaN or Infinity", pixelBounds);
            return; // skip - it's going to fail anyway
        }

        const mapDimensions = this.getMapLibreMap().getCanvas().getBoundingClientRect();  // Get map dimensions in pixels

        let latLonBounds = this.fromPx(pixelBounds);
        latLonBounds = es.util.bounds.sanitize(latLonBounds);

        // validate the converted lat/lon bounds too
        if (!latLonBounds || latLonBounds.length < 4 || latLonBounds.some(val => !isFinite(val))) {
            es.log.warn("ensurePixelBoundsVisible: Converted latLonBounds are invalid", latLonBounds, "from pixelBounds", pixelBounds);
            return; // skip - it's going to fail anyway
        }

        const isOffScreenX = (pixelBounds[0] >= mapDimensions.width  || pixelBounds[2] <= 0 || pixelBounds[0] < 0);
        const isOffScreenY = (pixelBounds[1] >= mapDimensions.height || pixelBounds[3] <= 0 || pixelBounds[1] < 0);

        this.ensureBoundsVisible(latLonBounds, isOffScreenX && isOffScreenY, false);
    }

    /**
     * Set non-visble padding area around map. used when a control is obscuring part of the ma e.g. legend on right of screen is expanded.
     * @param newPaddingPx [leftPadding, topPadding, rightPadding, bottomPadding] e.g. [10, 5, 293, 251]
     */
    public setPaddingPx(newPaddingPx: number[]){
        //es.log.log("setPaddingPx", newPaddingPx);
        this.paddingPx = newPaddingPx.slice(0, 4);
    }

    /**
     * Get padding settings to apply to camera
     * todo: currently, we set padding as [leftPadding, topPadding, rightPadding, bottomPadding]; - should make consistent (and clearer) adn use {top: 0, bottom: 0, left: 0, right: 0}
     */
    public getPaddingPx(){
        const paddingPx = this.paddingPx.slice();
        return {
            top: paddingPx[1],
            bottom: paddingPx[3],
            left: paddingPx[0],
            right: paddingPx[2]
        };
    }

    private onZoomToAreaMouseDown(e: MouseEvent) {
        const newEvent = {shiftKey: true, clientX: e.clientX, clientY:e.clientY, type: 'mousedown', button: e.button};
        // @ts-ignore
        this.map.map.handlers.handleEvent(newEvent);
    }

    private onZoomToAreaMouseUp() {
        this.cancelZoomToArea();
    }

    public beginZoomToArea(){
        this.setCursor("crosshair");
        this.eventChannel.fireEvent("onBeginZoomToArea");
        this.onZoomToAreaMouseDown = this.onZoomToAreaMouseDown.bind(this);
        this.onZoomToAreaMouseUp = this.onZoomToAreaMouseUp.bind(this);
        this._map.getCanvasContainer().addEventListener("mousedown", this.onZoomToAreaMouseDown);
        this._map.getCanvasContainer().addEventListener("mouseup", this.onZoomToAreaMouseUp);
    }

    public cancelZoomToArea(){
        this.setCursor("default");
        this.eventChannel.fireEvent("onFinishZoomToArea");
        this._map.getCanvasContainer().removeEventListener("mousedown", this.onZoomToAreaMouseDown);
        this._map.getCanvasContainer().removeEventListener("mouseup", this.onZoomToAreaMouseUp);
    }

    /**
     * Transforms Pixels to Positions (LatLon)
     * TODO: refactor to use NavUtils directly
     * @param coords
     * @param offset
     * @param length
     * @param result
     */
    public fromPx(coords: number[], offset?: number, length?: number, result?: number[]) {
        return NavUtils.fromPx(this, coords, offset, length, result);
    }

    /**
     * Transforms Positions (LatLon) to Pixels
     * TODO: refactor to use NavUtils directly
     * @param coords
     * @param offset
     * @param length
     * @param result
     */
    public toPx(coords: number[], offset?: number, length?: number, result?: number[]) {
        return NavUtils.toPx(this, coords, offset, length, result);
    }
}
